import { mdiFileDelimited } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Select } from '@mollybet/ui';
import { reactElementCSVFormatter } from 'common/utils/reactElementCSVFormatter';
import { transparentize } from 'polished';
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import styled, { CSSProperties, keyframes } from 'styled-components';
import { PAGE_SIZE_OPTIONS } from './config';

// #region Types
/** Types that can be sorted using > < = */
export type Comparible =
  | string
  | number
  | boolean
  | null
  | undefined
  | { valueOf(): number };

export type ValueFormatter<Row> = (row: Row, index: number) => React.ReactNode;

export type SortValueGenerator<Row, Return extends Comparible = Comparible> = (
  row: Row
) => Return;

export interface ColumnProp<Row> {
  key: string;
  title?: React.ReactNode;
  isSortable?: boolean;
  isResizeable?: boolean;
  width?: number;
  className?: string;
  valueFormatter?: ValueFormatter<Row>;
  sortValueGenerator?: SortValueGenerator<Row>;
  csvFormatter?: ValueFormatter<Row>;
}

export interface ColumnObject<Row> {
  key: string;
  title: React.ReactNode;
  isSortable: boolean;
  isResizeable: boolean;
  width: number | null;
  className?: string;
  valueFormatter: ValueFormatter<Row>;
  sortValueGenerator: SortValueGenerator<Row>;
  csvFormatter: ValueFormatter<Row>;
}

type ColumnOrder = string[];

interface ColumnMap<Row> {
  [key: string]: ColumnObject<Row>;
}

const isColumnObject = <Row,>(
  column: string | ColumnProp<Row>
): column is ColumnProp<Row> => !(typeof column === 'string');

const defaultValueFormatter =
  (key: string) =>
  <Row,>(row: Row) =>
    row[key];
const defaultSortValueGenerator =
  (key: string) =>
  <Row,>(row: Row) =>
    row[key];

/**
 * Columns props can be a list of strings, column objects or a combinations of
 * the two, so this function takes the props and converts them all to column
 * objects and provides default values for relevant fields.
 */
const createColumnObjectFromProp = <Row,>(
  column: string | ColumnProp<Row>
): ColumnObject<Row> => {
  // We build this during function
  // eslint-disable-next-line
  // @ts-ignore
  const _column: ColumnObject<Row> = {};

  if (isColumnObject(column)) {
    _column.key = column.key;
    _column.title = column.title || column.key;
    _column.isSortable = column.isSortable || true;
    _column.isResizeable = column.isResizeable || true;
    _column.width = column.width || null;
    _column.className = column.className || null;
    _column.valueFormatter =
      column.valueFormatter || defaultValueFormatter(column.key);
    _column.sortValueGenerator =
      column.sortValueGenerator || defaultSortValueGenerator(column.key);
    _column.csvFormatter = column.csvFormatter || _column.valueFormatter;
  } else {
    _column.key = column;
    _column.title = column;
    _column.isSortable = true;
    _column.isResizeable = true;
    _column.width = null;
    _column.className = null;
    _column.valueFormatter = defaultValueFormatter(column);
    _column.sortValueGenerator = defaultSortValueGenerator(column);
    _column.csvFormatter = defaultValueFormatter(column);
  }

  return _column;
};

interface FormattedRow {
  [key: string]: React.ReactNode;
}

export interface BaseTableProps<Row> {
  columns: (string | ColumnProp<Row>)[];
  rows: Row[];
  SubRow?: (props: {
    row: Row;
    index: number;
    style: CSSProperties;
  }) => JSX.Element;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
  isSortable?: boolean;
  isLoading?: boolean;
  error?: React.ReactNode;
  columnsAreResizeable?: boolean;
  paginate?: boolean;
  pagination?: {
    count: number;
    pageNo: number;
    pageSize: number;
    setPageNo: (pageNo: number) => void;
    setPageSize: (pageSize: number) => void;
  };
  totalColumns?: Set<string>;
  totalRowFilter?: (row: Row, key: string) => boolean;
  csvDownload?: boolean;
  csvName?: string;
  csvExtra?: string;
  extraButtons?: JSX.Element | JSX.Element[];
  defaultPageSize?: number;
  defaultPageNo?: number;
  className?: string;
  style?: CSSProperties;
}
// #endregion

// #region Elements
export const Table = styled.div`
  overflow: hidden;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.background};
  box-shadow: ${({ theme }) => theme.shadows[1]};
  width: fit-content;
`;

export const TableRow = styled.div`
  display: grid;
  border-bottom: 1px solid ${({ theme }) => theme.background};
  width: fit-content;
`;

interface TableCellProps {
  clickable?: boolean;
}

export const TableCell = styled.div<TableCellProps>`
  position: relative;
  padding: 0.5rem;
  font-size: 0.75rem;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : undefined)};
  user-select: ${({ clickable }) => (clickable ? 'none' : undefined)};
  overflow: auto;

  &:nth-child(even) {
    background-color: ${({ theme }) => theme.cardLevel2};
  }
  &:nth-child(odd) {
    background-color: ${({ theme }) => theme.cardLevel1};
  }
`;

export const TableHeaderCell = styled(TableCell)`
  text-align: center;
`;

export const TableHeader = styled.div`
  ${TableRow} {
    border-bottom: 1px solid ${({ theme }) => theme.disabled};

    ${TableCell} {
      font-size: 1rem;
      padding: 0.5rem 1rem 0.5rem 0.5rem;
    }
  }
`;

export const TableBody = styled.div`
  position: relative;
`;

export const SortIndicator = styled.span`
  display: inline-block;
  width: 0;
`;

export const TotalRow = styled(TableRow)`
  border-top: 1px solid ${({ theme }) => theme.foreground};
`;

export const TotalCell = styled(TableCell)`
  &:nth-child(n) {
    background-color: transparent;
  }
`;

export const PaginationRow = styled(TableRow)`
  width: auto;
  grid-template-columns: auto 1fr auto;
  background-color: ${({ theme }) => theme.cardLevel2};
  align-items: center;
`;

export const PageSizeCell = styled(TableCell)`
  text-align: left;

  &:nth-child(even),
  &:nth-child(odd) {
    background-color: transparent;
  }
`;

export const PageSizeSelect = styled(Select)`
  width: 6rem;
`;

export const PageNoCell = styled(TableCell)`
  text-align: center;

  &:nth-child(even),
  &:nth-child(odd) {
    background-color: transparent;
  }
`;

export const PageSelectCell = styled(TableCell)`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 0 0.5rem;

  &:nth-child(even),
  &:nth-child(odd) {
    background-color: transparent;
  }
`;

interface PageSelectButtonProps {
  selected?: boolean;
}

export const PageSelectButton = styled.div<PageSelectButtonProps>`
  cursor: pointer;
  font-weight: ${({ selected }) => (selected ? 'bold' : undefined)};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  transition: background-color 300ms;
  box-sizing: border-box;

  &:hover {
    background-color: ${({ theme }) => theme.cardLevel1};
  }
`;

export const Dragbar = styled.div`
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.background};
  border-width: 0 1px;
  position: absolute;
  height: fill-available;
  width: calc(0.5rem - 2px);
  top: 0;
  right: 0;
  background-color: ${({ theme }) => transparentize(0.9, theme.foreground)};
  cursor: col-resize;
`;

export const OuterContainer = styled.div`
  font-size: 0.75rem;
`;

export const NoRows = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4rem;
  width: 100%;
  grid-column: 1/-1;
  font-size: 1.5rem;
  box-sizing: border-box;
  padding: 1rem;
  color: ${({ theme }) => theme.cardLevel2};
`;

const LoadingKeyFrames = ({ theme }) => keyframes`
  0% {
    color: ${theme.foreground};
  }
  50% {
    color: ${theme.cardLevel2};
  }
  100% {
    color: ${theme.foreground};
  }
`;

interface LoadingProps {
  small?: boolean;
}

export const Loading = styled.div<LoadingProps>`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  grid-column: 1/-1;
  font-size: ${props => props.small ? '1rem' : '1.5rem'};
  box-sizing: border-box;
  padding: 1rem;
  background-color: ${({ theme }) => transparentize(0.3, theme.background)};
  animation: 2.5s infinite ${LoadingKeyFrames};
  z-index: 10;
`;

export const Error = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  grid-column: 1/-1;
  font-size: 1.5rem;
  box-sizing: border-box;
  padding: 1rem;
  background-color: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.danger};
`;

export const DownloadCsv = styled.a<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  font-size: 0.75rem;
  line-height: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  width: fit-content;
  border-radius: 4px;
  overflow: hidden;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.disabled : theme.primary};
  cursor: ${({ disabled }) => (disabled ? 'normal' : 'pointer')};
  padding: 1.25ex 1em;
  color: ${({ theme }) => theme.foreground} !important;
  text-decoration: none;
  margin: 0 1rem 1rem 0;
`;
// #endregion

// #region Components
/**
 * A base table that provides sorting, pagination and resizeable columns for a
 * given list of rows and list of columns.
 */
export const BaseTable = <Row,>({
  className,
  style,
  columns,
  rows,
  SubRow,
  defaultSortColumn,
  defaultSortDirection = 'asc',
  isSortable = true,
  isLoading = false,
  totalColumns,
  totalRowFilter,
  csvDownload = true,
  csvName = 'table',
  csvExtra = '',
  extraButtons,
  error,
  columnsAreResizeable = true,
  paginate = true,
  pagination,
  defaultPageSize = 25,
  defaultPageNo = 1,
}: BaseTableProps<Row>) => {
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [columnWidths, setColumnWidths] = useState<number[]>(
    columns.map((column) => (isColumnObject(column) ? column?.width : null))
  );
  const [relativeWidths, setRelativeWidths] = useState<number[]>([]);
  const [pageSize, setPageSize] = useState(
    pagination?.pageSize || defaultPageSize
  );
  const [pageNo, setPageNo] = useState(pagination?.pageNo || defaultPageNo);
  /** For displaying the page size dropdown in. */
  const outerContainer = useRef<HTMLDivElement>();
  const tableRef = useRef<HTMLDivElement>();
  /**
   * The column resizing uses window events for reliability, because of this we
   * have to use mutable values for storing event information then update state
   * accordingly.
   */
  const mutColumnWidths = useRef<number[]>([]);
  const resizeColumnIndex = useRef<number | null>(null);
  const prevX = useRef<number | null>(null);
  const throttled = useRef<boolean>(false);

  /**
   * Reset pageNo if the rows to be displayed changes
   */
  useEffect(() => {
    setPageNo(pagination?.pageNo || defaultPageNo);
    // eslint-disable-next-line
  }, [rows]);

  /**
   * Column sizes if columns change
   */
  useEffect(() => {
    setColumnWidths(
      columns.map((column) => (isColumnObject(column) ? column?.width : null))
    );
  }, [columns]);

  /**
   * Keeps the specified order of columns so they dont jump around between
   * renders.
   */
  const columnOrder = useMemo(
    (): ColumnOrder =>
      columns.map((column) => (isColumnObject(column) ? column.key : column)),
    [columns]
  );

  /** Maps column key to column object for quick access. */
  const columnMap = useMemo(
    (): ColumnMap<Row> =>
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [isColumnObject(column) ? column.key : column]:
            createColumnObjectFromProp(column),
        }),
        {}
      ),
    [columns]
  );

  /**
   * Since we allow both keys which are row properties and just identifiers
   * with custom value formatters, we should add a runtime check to make sure
   * people dont add indetifiers without an associated value formatter.
   *
   * Preferably typescript would do this for us but its unclear how to do this.
   */
  useEffect(() => {
    if (!rows?.length || !columns?.length) return;
    const allKeys: Set<string> = rows.reduce((acc, row) => {
      Object.keys(row).forEach((key) => key && acc.add(key));
      return acc;
    }, new Set<string>());

    columns.forEach((column) => {
      const columnKey = isColumnObject(column) ? column.key : column;
      const columnValueFormatter = isColumnObject(column)
        ? column.valueFormatter
        : undefined;
      if (!allKeys.has(columnKey) && columnValueFormatter == null) {
        console.error(
          'Column found with key that isnt in row keys and has no ' +
            'value formatter, this will cause an empty column.',
          '/nAll keys:',
          allKeys,
          '/nColumn:',
          column
        );
      }
    });
  }, [rows, columns]);

  /**
   * If pagination is provided externally, set internal state as they update.
   */
  useEffect(() => {
    if (pagination) {
      setPageSize(pagination?.pageSize || defaultPageSize);
      setPageNo(pagination?.pageNo || defaultPageNo);
    }
  }, [pagination, defaultPageSize, defaultPageNo]);

  /**
   * First we sort the rows based on the sortColumn and sortDirection state
   * values.
   */
  const sortedRows = useMemo(
    (): Row[] =>
      sortColumn
        ? [...(rows || [])].sort((rowA: Row, rowB: Row) => {
            const valueA = columnMap[sortColumn].sortValueGenerator(rowA);
            const valueB = columnMap[sortColumn].sortValueGenerator(rowB);

            if (valueA == null && valueB == null) return 0;
            if (valueA == null) return 1;
            if (valueB == null) return -1;
            if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
            if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
            return 0;
          })
        : [...(rows || [])],
    [rows, columnMap, sortColumn, sortDirection]
  );

  /** Second we paginate according to pageSize and pageNo state values. */
  const pageRows = useMemo((): Row[] => {
    return pagination || !paginate
      ? sortedRows
      : sortedRows.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [paginate, pagination, sortedRows, pageSize, pageNo]);

  /**
   * Third we create an ordered list of rows with there values preformatted by
   * the valueFormatter in the column object, so this isnt done every render.
   */
  const formattedRows = useMemo(
    (): FormattedRow[] =>
      pageRows.map((row: Row, index: number) => {
        return Object.values(columnMap).reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.valueFormatter(row, index),
          }),
          {}
        );
      }),
    [pageRows, columnMap]
  );

  /**
   * Calculate totals for required columns
   */
  const totalRow = useMemo((): Row | null => {
    if (!totalColumns?.size || !pageRows.length) return null;
    const totals = {} as Row;
    const typeMap = {} as { [key in keyof Row]: string };

    pageRows.forEach((row) =>
      Object.keys(row).forEach((key) => {
        if (!row?.[key]) return;
        if (totalRowFilter && !totalRowFilter(row, key)) return;
        if (typeof row[key] === 'string') typeMap[key] = 'string';

        const value = parseFloat(
          row[key]
            .toString()
            .match(/[0-9.-]/g)
            ?.join('')
        );

        if (!value || isNaN(value)) return;

        totals[key] = totals[key] || 0;
        totals[key] += value;
      })
    );

    Object.keys(totals).forEach((key) => {
      if (typeMap[key] === 'string') totals[key] = totals[key].toString();
    });

    return totals;
  }, [totalColumns, totalRowFilter, pageRows]);

  /** Calculates the 'auto' width of each column */
  useEffect(() => {
    if (formattedRows && tableRef.current) {
      const widths: number[] = [];
      const rows: HTMLElement[] = Array.from(
        tableRef.current.querySelectorAll('[role=row]')
      );
      const gridTemplateColumns = rows[0].style.gridTemplateColumns;
      const autoGridTemplateColumns = gridTemplateColumns
        .split(' ')
        .map(() => 'auto')
        .join(' ');

      // Set widths to auto for measuring
      rows.forEach((row: HTMLDivElement) => {
        row.style.gridTemplateColumns = autoGridTemplateColumns;
        row.style.whiteSpace = 'nowrap';
      });

      const cells: HTMLElement[] = Array.from(
        tableRef.current.querySelectorAll('[role=row] > *')
      );

      cells.forEach((column, _index) => {
        const index = _index % columnOrder.length;
        const width = Math.ceil(
          column.scrollWidth +
            // Seems to do some rounding internally, so +1 guarantees it
            // doesnt overflow
            1
        );
        if (width > (widths[index] || 0)) widths[index] = width;
      });

      // Set widths back to what they were
      rows.forEach((row: HTMLDivElement) => {
        row.style.gridTemplateColumns = gridTemplateColumns;
        row.style.whiteSpace = '';
      });

      setRelativeWidths(widths);
    }
  }, [tableRef, formattedRows, columnOrder]);

  /**
   * Generate the css grid-template-columns style property to define the size
   * of each column in the table.
   *
   * By default the columns will be evenly sized using 1fr and grid layout.
   *
   * Column widths can also be specified in the column object. Specifying a
   * column width and isResizeable to false will fix the column size.
   */
  const rowStyle = useMemo((): React.CSSProperties => {
    let templateColumns = '';
    for (let index = 0; index < columnOrder.length; index++) {
      templateColumns += ` ${
        columnWidths?.[index]
          ? `${columnWidths[index]}px`
          : relativeWidths?.[index]
          ? `${relativeWidths[index]}px`
          : '1fr'
      }`;
    }

    return { gridTemplateColumns: templateColumns };
  }, [columnOrder, columnWidths, relativeWidths]);

  /** Calculate the page count, for use later. */
  const pageCount = useMemo(
    (): number =>
      Math.ceil((pagination?.count || sortedRows.length) / pageSize),
    [pagination?.count, sortedRows, pageSize]
  );

  /**
   * Clicking on a header will toggle sorting by that column, so this generates
   * a click handler that sets the necessary state value for a given key.
   */
  const headerOnClickGenerator = useCallback(
    (key: string) => () => {
      if (!isSortable || !columnMap[key].isSortable) return;
      if (sortColumn === key) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(key);
        setSortDirection('asc');
      }
    },
    [isSortable, columnMap, sortColumn, sortDirection]
  );

  /**
   * Throttles mousemove events based on window.requestAnimation frame and if
   * throttle is passed updates resize properties and sets the column width
   * which gives the effect of resizeable columns.
   */
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (
      !throttled.current &&
      resizeColumnIndex.current != null &&
      prevX.current != null
    ) {
      throttled.current = true;
      const dx = event.clientX - prevX.current;
      const newColumnWidths = [...mutColumnWidths.current];
      newColumnWidths[resizeColumnIndex.current] += dx;
      prevX.current = event.clientX;
      mutColumnWidths.current = newColumnWidths;
      setColumnWidths(newColumnWidths);
      window.requestAnimationFrame(() => (throttled.current = false));
    }
  }, []);

  /** Resets resize properties and removes window event listeners. */
  const endColumnResize = useCallback(() => {
    throttled.current = false;
    resizeColumnIndex.current = null;
    prevX.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', endColumnResize);
    window.removeEventListener('mouseleave', endColumnResize);
  }, [handleMouseMove]);

  /**
   * Generates a mousedown event handler that sets initial values for resizing
   * and sets window listeners for a given key.
   *
   * If column widths arent already set, it will set the column widths to their
   * current px width. Leaving as 1fr would lead to janky resizing as changing
   * one column width would resize the others.
   */
  const dragbarOnMouseDownGenerator = useCallback(
    (index: number) => (event: React.MouseEvent<HTMLDivElement>) => {
      const row = event.currentTarget.parentElement.parentElement;
      const newColumnWidths = (
        columnWidths || Array(columnOrder.length).fill(undefined)
      ).map((width, index) => width || row.children[index]?.clientWidth);
      mutColumnWidths.current = newColumnWidths;
      setColumnWidths(newColumnWidths);
      resizeColumnIndex.current = index;
      prevX.current = event.clientX;
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', endColumnResize);
      window.addEventListener('mouseleave', endColumnResize);
    },
    [columnWidths, columnOrder, endColumnResize, handleMouseMove]
  );

  /**
   * Dragbar is inside header so we dont want clicking to also trigger sorting.
   */
  const dragbarOnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    []
  );

  /**
   * Generate encoded uri to download a csv file
   */
  const csvData = useMemo(() => {
    let s = 'data:text/csv;charset=utf-8,';

    s += columnOrder
      .map((key) => reactElementCSVFormatter(columnMap[key].title))
      .join(',');
    s += '\n';

    const csvFormattedRows = sortedRows.map((row) =>
      columnOrder
        .map((key, index) =>
          reactElementCSVFormatter(columnMap[key].csvFormatter(row, index))
        )
        .join(',')
    );

    if (totalColumns?.size) {
      csvFormattedRows.push(
        columnOrder
          .map((key, index) =>
            totalColumns?.has(key)
              ? reactElementCSVFormatter(
                  columnMap[key].csvFormatter(totalRow, index)
                )
              : ''
          )
          .join(',')
      );
    }

    const csvPaginatedRows =
      pagination || !paginate
        ? csvFormattedRows
        : csvFormattedRows.slice((pageNo - 1) * pageSize, pageNo * pageSize);

    s += csvPaginatedRows.join('\n');
    s += '\n';
    s += csvExtra;
    return s.replaceAll('#', '%23');
  }, [
    columnMap,
    columnOrder,
    totalColumns,
    totalRow,
    csvExtra,
    paginate,
    pagination,
    pageNo,
    pageSize,
    sortedRows,
  ]);

  return (
    <>
      <div>
        {csvDownload && (
          <DownloadCsv
            download={`${csvName.replace('.csv', '')}.csv`}
            href={csvData}
            disabled={!formattedRows.length}
          >
            <Icon path={mdiFileDelimited} size={0.675} />
            <div>Formatted CSV</div>
          </DownloadCsv>
        )}
        {extraButtons}
      </div>
      <Table role="table" ref={tableRef} className={className} style={style}>
        <TableHeader role="rowgroup">
          <TableRow role="row" style={rowStyle}>
            {columnOrder.map((key: string, index: number) => (
              <React.Fragment key={key}>
                <TableHeaderCell
                  role="columnheader"
                  onClick={headerOnClickGenerator(key)}
                  clickable={isSortable && columnMap[key].isSortable}
                  className={columnMap[key].className}
                >
                  {columnMap[key].title}{' '}
                  {!isSortable ? null : sortColumn === key ? (
                    <SortIndicator>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </SortIndicator>
                  ) : (
                    <SortIndicator />
                  )}
                  {columnsAreResizeable && columnMap[key].isResizeable && (
                    <Dragbar
                      onMouseDown={dragbarOnMouseDownGenerator(index)}
                      onClick={dragbarOnClick}
                    />
                  )}
                </TableHeaderCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody role="rowgroup">
          {formattedRows.map((row, index) => (
            <React.Fragment key={index}>
              <TableRow role="row" style={rowStyle}>
                {columnOrder.map((key: string) => (
                  <TableCell
                    role="cell"
                    key={key}
                    className={columnMap[key].className}
                  >
                    {row[key]}
                  </TableCell>
                ))}
              </TableRow>
              {SubRow && (
                <SubRow row={rows[index]} index={index} style={rowStyle} />
              )}
            </React.Fragment>
          ))}
          {!pageRows.length && <NoRows>No rows to display.</NoRows>}
          {isLoading && <Loading>Loading...</Loading>}
          {error && <Error>{error}</Error>}
          {!!totalColumns?.size && (
            <TotalRow role="row" style={rowStyle}>
              {columnOrder.map((key: string, index) => (
                <TotalCell
                  role="cell"
                  key={key}
                  className={columnMap[key].className}
                >
                  {totalRow[key] && totalColumns?.has(key)
                    ? columnMap[key].valueFormatter(totalRow, index)
                    : ''}
                </TotalCell>
              ))}
            </TotalRow>
          )}
          {paginate && (
            <PaginationRow>
              <PageSizeCell>
                <PageSizeSelect
                  value={{ value: pageSize, label: pageSize }}
                  options={PAGE_SIZE_OPTIONS}
                  label="Page Size"
                  onChange={({ value }) => {
                    (pagination?.setPageSize || setPageSize)(value);
                    (pagination?.setPageNo || setPageNo)(1);
                  }}
                  menuPortalTarget={outerContainer.current}
                  styles={{}}
                />
              </PageSizeCell>
              <PageNoCell>
                Page {pageNo} of {pageCount}
              </PageNoCell>
              <PageSelectCell>
                {pageCount > 9 ? (
                  <>
                    {pageNo - 4 > 1 && (
                      <PageSelectButton style={{ pointerEvents: 'none' }}>
                        ...
                      </PageSelectButton>
                    )}
                    {(() => {
                      const offset =
                        pageNo - 4 < 1
                          ? 0
                          : pageNo + 4 > pageCount
                          ? pageCount - 9
                          : pageNo - 5;
                      return Array.from({ length: 9 }, (_, index) => (
                        <PageSelectButton
                          onClick={() =>
                            (pagination?.setPageNo || setPageNo)(
                              index + offset + 1
                            )
                          }
                          selected={pageNo === index + offset + 1}
                          key={index + 1}
                        >
                          {index + offset + 1}
                        </PageSelectButton>
                      ));
                    })()}
                    {pageNo + 4 < pageCount && (
                      <PageSelectButton style={{ pointerEvents: 'none' }}>
                        ...
                      </PageSelectButton>
                    )}
                  </>
                ) : (
                  Array.from({ length: pageCount }, (_, index) => (
                    <PageSelectButton
                      onClick={() =>
                        (pagination?.setPageNo || setPageNo)(index + 1)
                      }
                      selected={pageNo === index + 1}
                      key={index + 1}
                    >
                      {index + 1}
                    </PageSelectButton>
                  ))
                )}
              </PageSelectCell>
            </PaginationRow>
          )}
        </TableBody>
      </Table>
      <OuterContainer id="outer-container" ref={outerContainer} />
    </>
  );
};
// #endregion
